/* istanbul ignore file - better tested visually */

import { classNames } from '@partful/shared/lib/classNames';
import { CSSProperties, forwardRef } from 'react';
import styles from './Typography.module.scss';

export type TypographyAppearance =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'
  | 'body'
  | 'label';

export type TypographyElementType = keyof JSX.IntrinsicElements;

type TypographyProps<T extends TypographyElementType> = JSX.IntrinsicElements[T] & {
  appearance?: TypographyAppearance;
  as?: T;
} & Partial<
    Pick<
      CSSProperties,
      | 'color'
      | 'fontWeight'
      | 'fontStyle'
      | 'letterSpacing'
      | 'lineBreak'
      | 'lineHeight'
      | 'overflow'
      | 'textAlign'
      | 'textDecoration'
      | 'textOverflow'
      | 'textWrap'
      | 'whiteSpace'
      | 'wordBreak'
      | 'wordSpacing'
      | 'wordWrap'
      | 'WebkitLineClamp'
    >
  >;

type TypographyFn = <T extends keyof JSX.IntrinsicElements>(props: TypographyProps<T>) => JSX.Element;

export const Typography = forwardRef<HTMLElement, TypographyProps<any>>(
  (
    {
      appearance = 'body',
      as: Tag = 'span',

      color,
      fontWeight,
      fontStyle,
      letterSpacing,
      lineBreak,
      lineHeight,
      overflow,
      textAlign,
      textDecoration,
      textOverflow,
      textWrap,
      whiteSpace,
      wordBreak,
      wordSpacing,
      wordWrap,
      WebkitLineClamp,

      style,
      className,
      children,
      ...rest
    },
    ref
  ) => (
    <Tag
      className={classNames(styles.base, styles[appearance], className)}
      style={{
        color,
        fontWeight,
        fontStyle,
        letterSpacing,
        lineBreak,
        lineHeight,
        overflow,
        textAlign,
        textDecoration,
        textOverflow,
        textWrap,
        whiteSpace,
        wordBreak,
        wordSpacing,
        wordWrap,
        ...(WebkitLineClamp
          ? // -webkit-line-clamp only works in conjuction with other css values being set
            {
              display: '-webkit-box',
              WebkitLineClamp,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
          : {}),
        ...style,
      }}
      {...rest}
      ref={ref}
    >
      {children}
    </Tag>
  )
) as TypographyFn;
